import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@material-ui/core";
import styled from "styled-components";
import _ from "lodash";

import { ASSET_RESOLUTION } from "common/constants";
import { IWorkspaceResourceAsset, IWorkspaceResourceFolder } from "common/interfaces";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { loadImage } from "js/core/utilities/promiseHelper";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { t } from "js/i18n";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import { Thumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import {
    SharedResourceContextList,
    SharedResourceFolderNavigateHandler,
    useSharedResourceActivityLabel,
} from "js/react/views/SharedResources/Components/SharedResourceContextList";
import {
    TeamResourceHiddenIndicator,
    TeamResourceIconMenu,
    TeamResourceThumbnailActions,
    TeamResourceThumbnailCard,
    TeamResourceThumbnailImageWrap,
    TeamResourceUsageBadge,
} from "js/react/views/SharedResources/Components/TeamResourceThumbnailCard";

const logger = getLogger(LogGroup.TEAMS);

const ThumbnailCardBar = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    height: auto;
    gap: 2px;
    padding: 0;
`;

const ThumbnailTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;

    .asset-title {
        line-height: 150%;
        display: flex;
        align-items: center;
        font-size: 14px;
        letter-spacing: 0px;
        font-weight: 600;
        color: #222;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        text-align: left;
        flex-grow: 2;
        text-transform: none;
        margin: 0px;
    }
`;

function getAssetUsageCount(asset: IWorkspaceResourceAsset) {
    const metadata = asset.metadata as IWorkspaceResourceAsset["metadata"] & {
        presentationCount?: number;
        usedInPresentations?: string[] | Record<string, boolean>;
        usedInPresentationsCount?: number;
        usageCount?: number;
    };

    if (typeof metadata.usedInPresentationsCount === "number") {
        return metadata.usedInPresentationsCount;
    }
    if (typeof metadata.presentationCount === "number") {
        return metadata.presentationCount;
    }
    if (typeof metadata.usageCount === "number") {
        return metadata.usageCount;
    }
    if (Array.isArray(metadata.usedInPresentations)) {
        return metadata.usedInPresentations.length;
    }
    if (metadata.usedInPresentations) {
        return Object.keys(metadata.usedInPresentations).length;
    }

    return 0;
}

export type AssetThumbnailProps = {
    item: IWorkspaceResourceAsset;
    onMouseDown: (event: React.MouseEvent) => void;
    onMouseUp: (event: React.MouseEvent) => void;
    onDoubleClick: (item: IWorkspaceResourceAsset) => void;
    onContextMenu: (event: React.MouseEvent) => void;
    selected: boolean;
    readOnly: boolean;
    renderContextMenuItems: (selection: IWorkspaceResourceAsset[]) => React.ReactNode;
    onMenuClick?: (item: IWorkspaceResourceAsset) => void;
    onVisibleChange?: (visible: boolean, item: IWorkspaceResourceAsset) => void;
    canUpgradeToManageSharedResources?: boolean;
    resourceFolders?: IWorkspaceResourceFolder[];
    onNavigateToFolder?: SharedResourceFolderNavigateHandler;
};

export function AssetThumbnail(props: AssetThumbnailProps) {
    const {
        item: asset,
        onMouseDown,
        onMouseUp,
        onDoubleClick,
        onContextMenu,
        selected,
        readOnly,
        renderContextMenuItems,
        onMenuClick,
        canUpgradeToManageSharedResources,
        onVisibleChange,
        resourceFolders = [],
        onNavigateToFolder,
    } = props;

    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
    const [isHovering, setIsHovering] = useState(false);
    const activityLabel = useSharedResourceActivityLabel(asset);
    const ref = useRef<HTMLDivElement>(null);
    const visible = useIntersectionObserver(ref);

    const fetchThumbnailUrl = async () => {
        const currentThumbnailUrl = thumbnailUrl;
        setThumbnailUrl(null);
        try {
            const assetModel = await ds.assets.getAssetById(asset.resourceId);
            if (assetModel.get("actualId") !== asset.metadata.actualId) {
                await assetModel.reload();
            }
            let url = await assetModel.getURL(ASSET_RESOLUTION.XSMALL);
            try {
                await loadImage(url);
            } catch {
                url = await assetModel.getURL(ASSET_RESOLUTION.XSMALL, true);
                await loadImage(url);
            }
            setThumbnailUrl(url);
        } catch (err) {
            logger.error(err, "[AssetThumbnail] getThumbnailUrl() failed");
            if (currentThumbnailUrl) {
                setThumbnailUrl(currentThumbnailUrl);
            }
        }
    };

    useEffect(() => {
        onVisibleChange?.(visible, asset);

        if (visible && _.isEmpty(thumbnailUrl)) {
            fetchThumbnailUrl();
        }
    }, [visible]);

    useEffect(() => {
        if (visible) {
            fetchThumbnailUrl();
        }
    }, [asset.metadata.actualId]);

    const usageCount = getAssetUsageCount(asset);
    const hoverActions = isHovering && !readOnly && (
        <TeamResourceThumbnailActions
            onClickCapture={() => onMenuClick?.(asset)}
            onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onMouseUp={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onDoubleClick={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onClick={event => event.stopPropagation()}
        >
            <TeamResourceIconMenu handleCloseMenu={() => setIsHovering(false)} fontSize="medium">
                {renderContextMenuItems([asset])}
            </TeamResourceIconMenu>
        </TeamResourceThumbnailActions>
    );

    return (
        <TeamResourceThumbnailCard
            $contentGap={8}
            $clickable={readOnly && !canUpgradeToManageSharedResources}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onDoubleClick={!readOnly ? onDoubleClick : undefined}
            onContextMenu={!readOnly ? onContextMenu : undefined}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            ref={ref}
            selected={selected}
            barVariant="custom"
            barContent={
                <ThumbnailCardBar>
                    <ThumbnailTitleRow>
                        <TruncatedTooltip title={asset.metadata.name} enterDelay={500} enterNextDelay={500}>
                            <div className="asset-title">{asset.metadata.name}</div>
                        </TruncatedTooltip>
                        {/* TODO: Show this once Team Asset usage is synced onto workspace resource metadata. */}
                        <TeamResourceUsageBadge
                            hidden
                            count={usageCount}
                            title={t("Team Asset")}
                            description={t("Only Librarians and Owners can edit. Changes apply everywhere this is used.")}
                        />
                    </ThumbnailTitleRow>
                    <SharedResourceContextList
                        activityLabel={activityLabel}
                        activityTooltip={activityLabel}
                        resource={asset}
                        resourceFolders={resourceFolders}
                        onNavigateToFolder={onNavigateToFolder}
                    />
                </ThumbnailCardBar>
            }
        >
            <TeamResourceThumbnailImageWrap>
                <Thumbnail
                    url={thumbnailUrl}
                    readOnly
                    style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                    imageStyle={!asset.metadata.isPublished ? { filter: "brightness(0.7)" } : {}}
                >
                    {!asset.metadata.isPublished && (
                        <TeamResourceHiddenIndicator>
                            <Icon>visibility_off</Icon>
                        </TeamResourceHiddenIndicator>
                    )}
                </Thumbnail>
                {hoverActions}
            </TeamResourceThumbnailImageWrap>
        </TeamResourceThumbnailCard>
    );
}
